import React from "react";
import dayjs from "dayjs";
import { Repeat, Check, X, Ban } from "lucide-react";
import { useLocalization } from "../hooks/useLocalization";

const STATUS_STYLES = {
  PENDING_REPLACEMENT_RESPONSE: "bg-amber-100 text-amber-700 border-amber-200",
  PENDING_MANAGER_APPROVAL: "bg-blue-100 text-blue-700 border-blue-200",
  APPROVED: "bg-emerald-100 text-emerald-700 border-emerald-200",
  REPLACEMENT_DECLINED: "bg-gray-100 text-gray-600 border-gray-200",
  REJECTED: "bg-red-100 text-red-700 border-red-200",
  CANCELLED: "bg-gray-100 text-gray-500 border-gray-200",
  EXPIRED: "bg-gray-100 text-gray-500 border-gray-200",
  REVALIDATION_REQUIRED: "bg-orange-100 text-orange-700 border-orange-200",
};

export function ShiftSwapRequestsList({ requests, myEmployeeId, onRespond, onCancel, responding }) {
  const { t } = useLocalization();
  if (requests.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="my-swap-requests-section">
      <p className="text-xs font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1.5"><Repeat size={13} /> {t("shiftSwap.myRequestsTitle")}</p>
      {requests.map((r) => {
        const isRequester = r.requester_employee_id === myEmployeeId;
        const isCandidate = !isRequester && r.candidate_employee_ids.includes(myEmployeeId) && r.status === "PENDING_REPLACEMENT_RESPONSE";
        const canCancel = isRequester && ["PENDING_REPLACEMENT_RESPONSE", "PENDING_MANAGER_APPROVAL"].includes(r.status);
        return (
          <div key={r.id} className="bg-white border border-gray-200 rounded-xl p-3.5" data-testid={`swap-request-card-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-gray-900">{dayjs(r.shift_snapshot.date).format("ddd, MMM D")} · {r.shift_snapshot.start_time}-{r.shift_snapshot.end_time}</p>
                <p className="text-xs text-gray-500">{r.shift_snapshot.store_location}</p>
                <p className="text-xs text-gray-400 mt-0.5">{isRequester ? t("shiftSwap.roleRequester") : t("shiftSwap.roleCandidate")}</p>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATUS_STYLES[r.status] || "bg-gray-100 text-gray-600 border-gray-200"}`} data-testid={`swap-status-badge-${r.id}`}>
                {t(`shiftSwap.status.${r.status}`)}
              </span>
            </div>
            {r.status === "REVALIDATION_REQUIRED" && r.eligibility_snapshot?.failure && (
              <p className="text-xs text-orange-600 mt-1.5">{r.eligibility_snapshot.failure}</p>
            )}
            {r.status === "REJECTED" && r.manager_rejection_reason && (
              <p className="text-xs text-red-600 mt-1.5">{t("shiftSwap.rejectionReasonLabel")}: {r.manager_rejection_reason}</p>
            )}
            {(isCandidate || canCancel) && (
              <div className="flex items-center gap-2 mt-2.5">
                {isCandidate && (
                  <>
                    <button
                      data-testid={`swap-accept-btn-${r.id}`}
                      disabled={responding === r.id}
                      onClick={() => onRespond(r.id, true)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50"
                    ><Check size={13} /> {t("shiftSwap.acceptSwap")}</button>
                    <button
                      data-testid={`swap-decline-btn-${r.id}`}
                      disabled={responding === r.id}
                      onClick={() => onRespond(r.id, false)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold hover:bg-gray-300 disabled:opacity-50"
                    ><X size={13} /> {t("shiftSwap.declineSwap")}</button>
                  </>
                )}
                {canCancel && (
                  <button
                    data-testid={`swap-cancel-btn-${r.id}`}
                    disabled={responding === r.id}
                    onClick={() => onCancel(r.id)}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 text-xs font-semibold hover:bg-gray-50 disabled:opacity-50"
                  ><Ban size={13} /> {t("shiftSwap.cancelRequest")}</button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
